import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CommentAttachmentDto } from '@inference.engine/workspace-api-client/web';
import { uploadToS3 } from '@/shared/api/upload-to-s3';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { i18n } from '@/shared/i18n';

const MAX_COMMENT_ATTACHMENTS = 5;
const MAX_COMMENT_ATTACHMENT_SIZE_BYTES = 25 * 1024 * 1024;

/**
 * Caller-supplied function that mints a presigned upload URL + asset id.
 * Comment-on-file routes this through the workspace-api so the resulting asset
 * id is tied to the caller's user/project/file scope and can be verified by
 * the backend at attach time.
 */
export type RequestCommentUploadFn = (file: File) => Promise<{
  url: string;
  fields: Record<string, string>;
  assetId: string;
}>;

type PendingStatus = 'uploading' | 'ready';

export interface PendingAttachment {
  localId: string;
  filename: string;
  mimeType: string;
  size: number;
  status: PendingStatus;
  // Present once the upload completes.
  assetId?: string;
  // Present while the upload is in-flight so the user can cancel/remove it.
  abortController?: AbortController;
  // For chips that were pre-loaded in edit mode (status: 'ready', no controller).
  isExisting?: boolean;
  // Local blob URL for image previews while uploading (revoked on remove/reset).
  // For pre-existing attachments in edit mode this is the signed download URL instead.
  previewUrl?: string;
}

export interface UseCommentAttachmentsResult {
  attachments: PendingAttachment[];
  assetIds: string[];
  hasInFlight: boolean;
  isAtLimit: boolean;
  add: (files: FileList | File[]) => Promise<void>;
  remove: (localId: string) => void;
  reset: () => void;
}

function uniqueLocalId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function seedFromExisting(existing?: CommentAttachmentDto[]): PendingAttachment[] {
  return (
    existing?.map((a) => ({
      localId: a.id,
      filename: a.filename,
      mimeType: a.mimeType,
      size: 0,
      status: 'ready' as const,
      assetId: a.assetId,
      isExisting: true,
      previewUrl: a.downloadUrl ?? undefined,
    })) ?? []
  );
}

function revokeIfBlob(url: string | undefined) {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
}

function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return i18n.t('common:utils.format.megabytes', {
    size: mb >= 10 ? mb.toFixed(0) : mb.toFixed(1),
  });
}

export function useCommentAttachments(
  requestUpload: RequestCommentUploadFn,
  initialAttachments?: CommentAttachmentDto[],
): UseCommentAttachmentsResult {
  const [attachments, setAttachments] = useState<PendingAttachment[]>(() =>
    seedFromExisting(initialAttachments),
  );

  // Latest-ref pattern so an unmount during upload still sees the rows to abort.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  useEffect(() => {
    return () => {
      for (const a of attachmentsRef.current) {
        a.abortController?.abort();
        revokeIfBlob(a.previewUrl);
      }
    };
  }, []);

  const requestUploadRef = useRef(requestUpload);
  requestUploadRef.current = requestUpload;

  const add = useCallback(async (incoming: FileList | File[]) => {
    const files = Array.from(incoming);
    if (files.length === 0) return;

    const slotsLeft = MAX_COMMENT_ATTACHMENTS - attachmentsRef.current.length;
    const overLimit = files.length - Math.max(0, slotsLeft);
    if (overLimit > 0) {
      notificationService.showWarning(
        i18n.t('projects:projectComment.useCommentAttachments.youCanAttachAtMost', {
          max: MAX_COMMENT_ATTACHMENTS,
          count: overLimit,
        }),
      );
    }

    const accepted: File[] = [];
    for (const file of files.slice(0, Math.max(0, slotsLeft))) {
      if (file.size > MAX_COMMENT_ATTACHMENT_SIZE_BYTES) {
        notificationService.showError(
          i18n.t('projects:projectComment.useCommentAttachments.attachmentsMustBeUnder', {
            filename: file.name,
            size: formatBytes(file.size),
            max: formatBytes(MAX_COMMENT_ATTACHMENT_SIZE_BYTES),
          }),
        );
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length === 0) return;

    const newRows: PendingAttachment[] = accepted.map((file) => {
      const mimeType = file.type || 'application/octet-stream';
      return {
        localId: uniqueLocalId(),
        filename: file.name,
        mimeType,
        size: file.size,
        status: 'uploading',
        abortController: new AbortController(),
        previewUrl: mimeType.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      };
    });

    setAttachments((prev) => [...prev, ...newRows]);

    await Promise.all(
      newRows.map(async (row, idx) => {
        const file = accepted[idx];
        try {
          const { url, assetId, fields } = await requestUploadRef.current(file);

          await uploadToS3(url, fields, file, row.abortController?.signal);

          setAttachments((prev) =>
            prev.map((a) =>
              a.localId === row.localId
                ? { ...a, status: 'ready', assetId, abortController: undefined }
                : a,
            ),
          );
        } catch (err) {
          // Aborts surface as cancellation — drop silently; the user already removed the chip.
          if (row.abortController?.signal.aborted) return;
          logger.error('Comment attachment upload failed', err);
          notificationService.showError(
            i18n.t('projects:projectComment.useCommentAttachments.failedToUpload', {
              filename: row.filename,
            }),
          );
          revokeIfBlob(row.previewUrl);
          setAttachments((prev) => prev.filter((a) => a.localId !== row.localId));
        }
      }),
    );
  }, []);

  const remove = useCallback((localId: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.localId === localId);
      target?.abortController?.abort();
      revokeIfBlob(target?.previewUrl);
      return prev.filter((a) => a.localId !== localId);
    });
  }, []);

  const reset = useCallback(() => {
    setAttachments((prev) => {
      for (const a of prev) {
        a.abortController?.abort();
        revokeIfBlob(a.previewUrl);
      }
      return [];
    });
  }, []);

  const assetIds = useMemo(
    () =>
      attachments.filter((a) => a.status === 'ready' && a.assetId).map((a) => a.assetId as string),
    [attachments],
  );

  const hasInFlight = attachments.some((a) => a.status === 'uploading');
  const isAtLimit = attachments.length >= MAX_COMMENT_ATTACHMENTS;

  return { attachments, assetIds, hasInFlight, isAtLimit, add, remove, reset };
}
